import type {
  AttendanceChartPointByDay,
  AttendanceChartPointBySession,
  ClassroomAttendanceChartResponse,
} from '../BackendInterface';

export interface AttendanceChartPoint {
  label: string;
  periodLabel: string;
  presentCount: number;
  absentCount: number;
  total: number;
  attendanceRate: number;
}

function formatShortDate(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function roundAttendanceRate(rate: number): number {
  return Math.round(rate * 10) / 10;
}

function mapDayPoints(points: AttendanceChartPointByDay[]): AttendanceChartPoint[] {
  return [...points]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((point) => {
      const date = new Date(`${point.date}T00:00:00`);

      return {
        label: formatShortDate(date),
        periodLabel: point.date,
        presentCount: point.presentCount,
        absentCount: point.absentCount,
        total: point.total,
        attendanceRate: roundAttendanceRate(point.attendanceRate),
      };
    });
}

function mapSessionPoints(points: AttendanceChartPointBySession[]): AttendanceChartPoint[] {
  return [...points]
    .sort((a, b) => a.sessionNumber - b.sessionNumber)
    .map((point) => ({
      label: `Session ${point.sessionNumber}`,
      periodLabel: `Session ${point.sessionNumber}`,
      presentCount: point.presentCount,
      absentCount: point.absentCount,
      total: point.total,
      attendanceRate: roundAttendanceRate(point.attendanceRate),
    }));
}

export function mapAttendanceChartResponseToChartData(
  response: ClassroomAttendanceChartResponse | null,
): AttendanceChartPoint[] {
  if (!response) {
    return [];
  }

  if (response.groupBy === 'session') {
    return mapSessionPoints(response.points as AttendanceChartPointBySession[]);
  }

  return mapDayPoints(response.points as AttendanceChartPointByDay[]);
}
